<template>
  <div v-rect-loading="loading" class="company-premium-management">
    <div class="header">
      <div class="company-name">
        <span class="name">{{ getCompanyName() }}</span>
        <span class="tip">{{ $t(`${i18nPrefix}.label.premiumSettingTip`) }}</span>
      </div>
      <div class="rest-expire-date">
        <el-button class="reset-expire-date-btn" @click="handleClickResetExpireDate">{{ $t(`${i18nPrefix}.label.resetAllExpireDate`) }}</el-button>
      </div>
    </div>
    <el-collapse v-if="premiumList.length" v-model="premiumCollapseActiveList" class="premium-collapse">
      <el-collapse-item
        v-for="(item, index) in premiumList"
        class="premium-collapse-item"
        :key="index"
        :title="item.label"
        :name="item.category"
      >
        <div v-for="(premium, premiumIndex) in item.premiumList" class="premium-item" :key="premiumIndex">
          <PremiumEditor
            :premium="premium"
            @change="handlePremiumChange"
          >
            <template #companyAccountSlot></template>
            <template #userNumSlot="{ premiumDetail }">
              <el-input v-model="premiumDetail.value" class="user-num-input"></el-input>
              <el-button class="user-num-btn" @click="handleClickViewUser">{{ $t('common.btn.view') }}</el-button>
            </template>
            <template #companyListAdsLevelSlot="{ premiumDetail }">
              <el-radio v-model="premiumDetail.value" label="1">{{ $t(`${i18nPrefix}.label.companyListAdsFirstLevel`) }}</el-radio>
              <el-radio v-model="premiumDetail.value" label="2">{{ $t(`${i18nPrefix}.label.companyListAdsSecondLevel`) }}</el-radio>
              <el-radio v-model="premiumDetail.value" label="0">{{ $t(`${i18nPrefix}.label.companyListAdsThirdLevel`) }}</el-radio>
            </template>
            <template #rateManagementDetailSlot="{ premiumDetail }">
              <el-checkbox v-model="premiumDetail.invite_hm_review" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.hmReviewingSystem`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.interview_scheduling" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.interviewSchedulingSystem`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.quickly_create_teams_meeting" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.quicklyCreateTeamsMeeting`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.view_hiring_manager_availability" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.viewHmAvailability`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.send_interview_sms" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.sendInterviewSMS`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.automated_scheduler" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.automatedScheduler`) }}</el-checkbox>
            </template>
            <template #submitResumeDetailSlot="{ premiumDetail }">
              <div class="set-referral-days">
                <span class="label">{{ $t(`${i18nPrefix}.label.periodicity`) }}</span>
                <el-input v-model="premiumDetail.referral_days" class="days" :disabled="handlePremiumDisabled(premiumDetail)"></el-input>
                <span class="label">{{ $t(`${i18nPrefix}.label.day`) }}</span>
              </div>
            </template>
            <template #referralDetailSlot="{ premiumDetail }">
              <div class="referral-mode">
                <el-radio v-model="premiumDetail.value" label="1" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.referralTypeRegular`) }}</el-radio>
                <el-radio v-model="premiumDetail.value" label="2" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.referralTypeLite`) }}</el-radio>
              </div>
              <div v-if="isRootCompany" class="referral-employee-setting">
                <el-radio v-model="premiumDetail.referral_share" label="0" :disabled="!premiumDetail.isEnabled || !premiumDetail.can_edit_referral_share">{{ $t(`${i18nPrefix}.label.employeeIndependency`) }}</el-radio>
                <el-radio v-model="premiumDetail.referral_share" label="1" :disabled="!premiumDetail.isEnabled || !premiumDetail.can_edit_referral_share">{{ $t(`${i18nPrefix}.label.employeeSharing`) }}</el-radio>
              </div>
              <el-checkbox v-if="isRootCompany" v-model="premiumDetail.referral_employee_apply_internal_job" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.allowEmployeeApplyJob`) }}</el-checkbox>
              <el-checkbox v-if="isRootCompany" v-model="premiumDetail.referral_points_rewards" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.pointsRewords`) }}</el-checkbox>
            </template>
            <template #companyListAdsLevelDetailSlot="{ premiumDetail }">
              <div v-if="premiumDetail.value === '0'"></div>
              <div v-if="premiumDetail.value === '1'" class="company-list-first-level">
                <div class="upload-setting primary-image">
                  <div class="title">
                    <span class="label">{{ $t(`${i18nPrefix}.label.primaryAdsImage`) }}</span>
                    <div class="image-selector">
                      <el-button class="select-file" @click="handleSelectPrimaryAdsImage">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
                      <input ref="primaryAdsFileSelector" class="file-selector" type="file" :accept="acceptImageFileTypeList.join(',')" @change="handlePrimaryAdsImageChange"/>
                      <div class="tip">{{ $t(`${i18nPrefix}.label.adsImageTip`) }}</div>
                    </div>
                  </div>
                  <div v-if="previewPrimaryAdsImage" class="image">
                    <img class="pre-image" :src="previewPrimaryAdsImage">
                  </div>
                </div>
                <div class="upload-setting sub-image">
                  <div class="title">
                    <span class="label">{{ $t(`${i18nPrefix}.label.subAdsImage`) }}</span>
                    <div class="image-selector">
                      <el-button class="select-file" @click="handleSelectSubAdsImage">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
                      <input ref="subAdsFileSelector" class="file-selector" type="file" :accept="acceptImageFileTypeList.join(',')" @change="handleSubAdsImageChange"/>
                      <div class="tip">{{ $t(`${i18nPrefix}.label.adsImageTip`) }}</div>
                    </div>
                  </div>
                  <div v-if="previewSubAdsImage" class="image">
                    <img class="pre-image" :src="previewSubAdsImage">
                  </div>
                </div>
              </div>
              <div v-if="premiumDetail.value !== '0'" class="company-list-second-level">
                <span class="label">{{ $t(`${i18nPrefix}.label.adsLink`) }}</span>
                <div class="link-content">
                  <el-input v-model="companyAdsInfo.ads_target_link" class="link"></el-input>
                  <span class="tip">{{ $t(`${i18nPrefix}.label.adsLinkExample`) }}</span>
                </div>
              </div>
            </template>
            <template #postJobDetailSlot="{ premiumDetail }">
              <div class="auto-post-job">
                <div class="label">{{ $t(`${i18nPrefix}.label.autoPostJob`) }}</div>
                <div class="set-email">
                  <span class="label">{{ $t(`${i18nPrefix}.label.settingEmail`) }}</span>
                  <el-select v-model="postJobSelectedAccountList" multiple filterable class="select-account" :disabled="handlePremiumDisabled(premiumDetail)">
                    <el-option
                      v-for="(option, index) in accountList"
                      :key="index"
                      :label="option.email_address"
                      :value="option.id"
                    ></el-option>
                  </el-select>
                </div>
                <div class="select-channels">
                  <span class="label">{{ $t(`${i18nPrefix}.label.settingChannel`) }}</span>
                  <el-select v-model="postJobSelectedChannelList" multiple filterable class="select-channel" :disabled="handlePremiumDisabled(premiumDetail)">
                    <el-option
                      v-for="(option, index) in jobChannels"
                      :key="index"
                      :label="option.i18nName"
                      :value="option.id"
                    ></el-option>
                  </el-select>
                </div>
              </div>
              <div class="manual-post-job">
                <div class="label">{{ $t(`${i18nPrefix}.label.manualPostJob`) }}</div>
                <div class="select-channels">
                  <span class="label">{{ $t(`${i18nPrefix}.label.settingChannel`) }}</span>
                  <el-select v-model="manualPostJobSelectedChannelList" multiple filterable class="select-channel" :disabled="handlePremiumDisabled(premiumDetail)">
                    <el-option
                      v-for="(option, index) in manualPostUseChannels"
                      :key="index"
                      :label="option.i18nName"
                      :value="option.id"
                    ></el-option>
                  </el-select>
                </div>
              </div>
            </template>
            <template #customJobDetailSlot="{ premiumDetail }">
              <el-checkbox v-model="premiumDetail.custom_job_detail_company_level" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.companyLevel`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.custom_job_detail_job_level" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.jobLevel`) }}</el-checkbox>
            </template>
            <template #colorCustomDetailSlot="{ premiumDetail }">
              <div class="color-custom-content">
                <!-- <div class="set-color">
                  <span class="label">{{ $t(`${i18nPrefix}.label.settingColor`) }}</span>
                  <el-color-picker v-model="premiumDetail.color" size="mini" :disabled="handlePremiumDisabled(premiumDetail)"></el-color-picker>
                </div> -->
                <el-checkbox v-model="premiumDetail.hidden_web_page_bottom_statement" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.hiddenWebPageBottomStatement`) }}</el-checkbox>
              </div>
            </template>
            <template #vendorManagementDetailSlot="{ premiumDetail }">
              <el-checkbox v-model="premiumDetail.agency_just_show" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.agencyJustShow`) }}</el-checkbox>
              <div class="set-agency-management-days">
                <span class="label">{{ $t(`${i18nPrefix}.label.periodicity`) }}</span>
                <el-input v-model="premiumDetail.vendor_management_days" class="days" :disabled="handlePremiumDisabled(premiumDetail)"></el-input>
                <span class="label">{{ $t(`${i18nPrefix}.label.day`) }}</span>
              </div>
            </template>
            <template #jobPosterDetailSlot="{ premiumDetail }">
              <el-checkbox v-model="premiumDetail.job_poster_image" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.jobPosterImage`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.job_poster_video" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.jobPosterVideo`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.ambassador_poster" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.ambassadorPoster`) }}</el-checkbox>
            </template>
            <template #satisfactionSurveyDetailSlot="{ premiumDetail }">
              <el-checkbox v-model="premiumDetail.satisfaction_survey_candidate" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.satisfactionSurveyCandidate`) }}</el-checkbox>
              <el-checkbox v-model="premiumDetail.satisfaction_survey_hm" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.satisfactionSurveyHm`) }}</el-checkbox>
            </template>
            <template #dataPurgeDetailSlot="{ premiumDetail }">
              <div class="data-purge-type">
                <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.candidateConfirmToDeleteTip`)">
                  <el-radio v-model="premiumDetail.data_purge_type" label="1" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.candidateConfirmToDelete`) }}</el-radio>
                </el-tooltip>
                <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.candidateConfirmToStayTip`)">
                  <el-radio v-model="premiumDetail.data_purge_type" label="2" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.candidateConfirmToStay`) }}</el-radio>
                </el-tooltip>
                <el-radio v-model="premiumDetail.data_purge_type" label="3" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.label.deleteDirectly`) }}</el-radio>
              </div>
              <div class="set-data-purge-days">
                <span class="label">{{ $t(`${i18nPrefix}.label.periodicity`) }}</span>
                <el-input v-model="premiumDetail.data_purge_days" class="days" :disabled="handlePremiumDisabled(premiumDetail)"></el-input>
                <span class="label">{{ $t(`${i18nPrefix}.label.day`) }}</span>
              </div>
            </template>
            <template #smsSendDetailSlot="{ premiumDetail }">
              <div class="sms-send-content">
                <el-checkbox v-model="premiumDetail.consent_sms_send" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.premiumType.consent_sms_send`) }}</el-checkbox>
                <el-checkbox v-model="premiumDetail.other_sms_send" label="1" true-label="1" false-label="0" :disabled="handlePremiumDisabled(premiumDetail)">{{ $t(`${i18nPrefix}.premiumType.other_sms_send`) }}</el-checkbox>
              </div>
            </template>
          </PremiumEditor>
        </div>
      </el-collapse-item>
    </el-collapse>
    <div class="footer">
      <el-button class="footer-btn cancel-btn" @click="handleCancelBtnClick">{{ $t('common.btn.cancel') }}</el-button>
      <el-button class="footer-btn save-btn" @click="handleSaveBtnClick">{{ $t('common.btn.save') }}</el-button>
    </div>
    <DialogWrapper
      class="company-user-list-dialog"
      hideFooter
      :show.sync="showCompanyUserList"
      :loading="loadingUserList"
      :titleI18nKey="`${i18nPrefix}.label.viewCompanyUserList`"
    >
      <TableList
        class="user-management-list"
        row-key="id"
        noAction
        noHead
        :listData="userList"
        :tableColumns="tableColumns"
        :showCreateBtn="false"
        :pagination="pagination"
        :defaultSort="defaultOrder"
        @pagination-change="handlePaginationChange"
        @sort-change="handleSortChange"
      >
        <template #disabled="{ data, row }">
          <el-switch :value="data" @change="onEnabledChange($event, row)"></el-switch>
        </template>
      </TableList>
    </DialogWrapper>
    <DialogWrapper
      class="rest-expire-date-dialog"
      :show.sync="showResetExpireDate"
      :titleI18nKey="`${i18nPrefix}.label.resetAllExpireDate`"
      @save="handleAllPremiumExpireDate"
    >
      <el-date-picker
        v-model="currentPremiumExpireDate"
        class="rest-expire-date-picker"
        type="date"
        value-format="yyyy-MM-dd"
        :picker-options="datePickerOptions"
        :placeholder="$t(`${i18nPrefix}.placeholder.selectDate`)"
      >
      </el-date-picker>
    </DialogWrapper>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import { addQueryInPath, imageToBase64 } from '@/utils/common'
import PremiumEditor from './components/premiumEditor.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { addI18nProperty } from '@/i18n'
import { mixedRouterPush } from '@/router/routes'

// 这些收费项 key: 收费项, value: 收费项的子项在渲染时使用的插槽名称
const HAS_DETAIL_PREMIUM_MAP = {
  'rate-management': 'rateManagementDetailSlot',
  submit_resume: 'submitResumeDetailSlot',
  referral: 'referralDetailSlot',
  company_list_level: 'companyListAdsLevelDetailSlot',
  post_job: 'postJobDetailSlot',
  custom_job_detail: 'customJobDetailSlot',
  color_customization: 'colorCustomDetailSlot',
  vendor_management: 'vendorManagementDetailSlot',
  job_poster: 'jobPosterDetailSlot',
  satisfaction_survey: 'satisfactionSurveyDetailSlot',
  data_purge: 'dataPurgeDetailSlot',
  sms_send: 'smsSendDetailSlot'
}

// 这些收费项不是开关而是有自己的一部分显示逻辑 key: 收费项, value: 插槽名称, eg: user_num
const HAS_CUSTOM_HEADER_PREMIUM_MAP = {
  user_num: 'userNumSlot',
  company_list_level: 'companyListAdsLevelSlot',
  company_expire: 'companyAccountSlot'
}

// 这里是每个收费项中有数字值需要校验 key: 收费项, value: premiumInfo 中需要是数字的字段名, eg: user_num: 'value' user_num 中 value 必须是数字
const HAS_NUMBER_VALIDATE_PREMIUM_MAP = {
  user_num: 'value',
  vendor_management: 'vendor_management_days',
  data_purge: 'data_purge_days',
  job_num: 'value'
}

export default {
  components: {
    PremiumEditor,
    DialogWrapper,
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.company.companyApprovalManagement',
      loading: false,
      premiumCollapseActiveList: [],
      companyPremium: {},
      company: {
        name: null,
        cn_name: null,
        i18nName: null
      },
      jobChannels: [],
      accountList: [],
      isRootCompany: false,
      acceptImageFileTypeList: ['image/png', 'image/jpg'],
      companyAdsInfo: {
        ads_primary_image_info: {
          image: null,
          image_base64: null,
          image_file_name: null
        },
        ads_sub_image_info: {
          image: null,
          image_base64: null,
          image_file_name: null
        },
        ads_target_link: null
      },
      postJobSelectedAccountList: [],
      postJobSelectedChannelList: [],
      manualPostJobSelectedChannelList: [],
      currentPremiumExpireDate: null,
      datePickerOptions: {
        disabledDate (time) {
          return time.getTime() < new Date().getTime() - 86400000
        }
      },
      showResetExpireDate: false,
      premiumCategories: [
        'basic_config',
        'recruiting_config',
        'career_brand_config',
        'referral_config',
        'interview_config',
        'crm_config',
        'onboarding_config',
        'data_config',
        'ai_config'
      ],
      showCompanyUserList: false,
      loadingUserList: false,
      userList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
        pagerCount: 3
      },
      order: '-status',
      originPremiumData: {},
      manualPostUseChannels: []
    }
  },
  computed: {
    companyId () {
      return this.$route.params.companyId
    },
    premiumList () {
      const vm = this
      _.forEach(this.companyPremium, (val, key) => {
        Object.defineProperty(val, 'name', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.$t(`${vm.i18nPrefix}.premiumType.${key}`)
          }
        })
        val.type = key
        Object.defineProperty(val, 'isEnabled', {
          enumerable: true,
          configurable: true,
          get () {
            if (key === 'rate-management') {
              if (val.value === '0') {
                return false
              }
              return val.value === '1' || val.invite_hm_review === '1' || val.view_hiring_manager_availability === '1' || val.quickly_create_teams_meeting === '1' || val.interview_scheduling === '1'
            }
            if (key === 'sms_send') {
              if (val.value === '0') {
                return false
              }
              return val.value === '1' || val.consent_sms_send === '1' || val.other_sms_send === '1'
            }
            if (key === 'referral') {
              return val.value === '1' || val.value === '2'
            }
            if (key === 'company_list_level') {
              return val.value === '0' || val.value === '1' || val.value === '2'
            }
            if (key === 'post_job') {
              return val.value !== '0'
            }
            if (key === 'color_customization') {
              return val.value !== '0'
            }
            if (key === 'user_num') {
              return val.value !== '0'
            }
            if (key === 'job_num') {
              return val.value !== '0'
            }
            if (key === 'company_expire') {
              return moment(val.expire_date).isAfter(moment())
            }
            return val.value === '1'
          },
          set (isEnabled) {
            if (_.includes(['user_num', 'job_num', 'company_expire'], key)) {
              return
            }
            if (isEnabled) {
              val.value = '1'
            } else {
              val.value = '0'
            }
          }
        })
        if (_.includes(_.keys(HAS_DETAIL_PREMIUM_MAP), key)) {
          val.detailSlot = HAS_DETAIL_PREMIUM_MAP[key]
        }

        if (key === 'job_num') {
          val.elType = 'input'
        } else if (_.includes(_.keys(HAS_CUSTOM_HEADER_PREMIUM_MAP), key)) {
          val.headerSlot = HAS_CUSTOM_HEADER_PREMIUM_MAP[key]
        } else {
          val.elType = 'switch'
        }
      })
      return _.chain(this.companyPremium).filter(item => {
        if (item.is_only_root_company) {
          return this.isRootCompany
        } else {
          return true
        }
      }).groupBy(item => item.category).map((premiums, key) => {
        return {
          label: this.$t(`${this.i18nPrefix}.premiumCategories.${key}`),
          premiumList: _.orderBy(premiums, 'order'),
          order: _.indexOf(this.premiumCategories, key)
        }
      }).orderBy('order').value()
    },
    previewPrimaryAdsImage () {
      if (this.companyAdsInfo.ads_primary_image_info.image_base64) {
        return this.companyAdsInfo.ads_primary_image_info.image_base64
      }
      return this.$DjangoMediaUrl(addQueryInPath(this.companyAdsInfo.ads_primary_image_info.image, { file_time_stamp: new Date().valueOf() }))
    },
    previewSubAdsImage () {
      if (this.companyAdsInfo.ads_sub_image_info.image_base64) {
        return this.companyAdsInfo.ads_sub_image_info.image_base64
      }
      return this.$DjangoMediaUrl(addQueryInPath(this.companyAdsInfo.ads_sub_image_info.image, { file_time_stamp: new Date().valueOf() }))
    },
    defaultOrder () {
      const order = this.order
      if (order.includes('-')) {
        return {
          prop: order.split('-')[1],
          order: 'descending'
        }
      } else {
        return {
          prop: order,
          order: 'ascending'
        }
      }
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t('admin.company.userManagement.label.name'),
          dataKey: 'full_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t('admin.company.userManagement.label.company'),
          dataKey: 'companyName',
          showOverflowTooltip: true
        },
        {
          label: this.$t('admin.company.userManagement.label.email'),
          dataKey: 'email_address',
          showOverflowTooltip: true
        },
        {
          label: this.$t('admin.company.userManagement.label.role'),
          dataKey: 'role_all_titles',
          showOverflowTooltip: true,
          formatter (data) {
            return data.join(',')
          }
        },
        {
          label: this.$t('admin.company.userManagement.label.status'),
          dataKey: 'status',
          slotName: 'disabled',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          }
        },
        {
          label: this.$t('admin.company.userManagement.label.updateTime'),
          dataKey: 'updated_time',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          showOverflowTooltip: true,
          formatter (data) {
            return moment(data).format('YYYY-MM-DD')
          }
        }
      ]
      return columns
    }
  },
  created () {
    this.loadCompanyPremiumInfo()
  },
  mounted () {
    window.addEventListener('beforeunload', this.beforeUnloadHandler, false)
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler, false)
  },
  methods: {
    ...mapActions('companyManagement', ['getCompanyPremiumInfo', 'saveCompanyPremium']),
    ...mapActions('userManagement', ['getUserList', 'updateUser']),
    async loadCompanyPremiumInfo () {
      if (!this.companyId) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getCompanyPremiumInfo({ companyId: this.companyId })
        if (resp) {
          this.jobChannels = _.map(resp.job_channels, channel => {
            return addI18nProperty(channel, 'i18nName', 'cn_name', 'name')
          })
          this.manualPostUseChannels = _.filter(this.jobChannels, channel => _.includes(resp.manual_post_job_channel_ids, channel.id))
          this.accountList = resp.account_list
          const defaultSelectAccount = _.find(this.accountList, account => account.email_address === 'grace.zhu+ajinga@ajinga.com')
          this.originPremiumData = resp
          this.companyPremium = _.cloneDeep(resp.company_premium)
          this.companyAdsInfo.ads_primary_image_info.image = resp.ads_image
          this.companyAdsInfo.ads_sub_image_info.image = resp.ads_image_sub
          this.companyAdsInfo.ads_target_link = resp.ads_target_link
          this.company = resp.company
          addI18nProperty(this.company, 'i18nName', 'cn_name', 'name')
          this.isRootCompany = resp.company.is_root_company
          if (defaultSelectAccount) {
            this.postJobSelectedAccountList.push(defaultSelectAccount.id)
          }
          // if (this.companyPremium.color_customization?.value) {
          //   const premiumValue = this.companyPremium.color_customization.value
          //   const color = premiumValue?.color
          //   const hiddenWebPageBottomStatement = premiumValue?.hidden_web_page_bottom_statement
          //   const value = color || hiddenWebPageBottomStatement === '1'
          //   this.companyPremium.color_customization.value = value ? '1' : '0'
          // }
          if (this.companyPremium.post_job?.value) {
            const postJobValue = this.companyPremium.post_job?.value
            this.postJobSelectedAccountList = _.uniq(_.concat(this.postJobSelectedAccountList, _.chain(postJobValue.account_ids).split(',').filter().map(item => _.parseInt(item)).value()))
            this.postJobSelectedChannelList = _.chain(postJobValue.channel_ids).split(',').filter().map(item => _.parseInt(item)).value()
            this.manualPostJobSelectedChannelList = _.chain(postJobValue.manual_channel_ids).split(',').filter().map(item => _.parseInt(item)).value()
            this.originPremiumData.postJobSelectedAccountList = _.clone(this.postJobSelectedAccountList)
            this.originPremiumData.postJobSelectedChannelList = _.clone(this.postJobSelectedChannelList)
            this.originPremiumData.manualPostJobSelectedChannelList = _.clone(this.manualPostJobSelectedChannelList)
          }
        }
      } finally {
        this.loading = false
      }
    },
    getCompanyName () {
      const name = this.company.i18nName
      if (name) {
        return name
      }
      return this.company.name || this.company.cn_name
    },
    handlePremiumChange (premium) {
      _.set(this.companyPremium, premium.type, premium)
    },
    handlePremiumDisabled (premiumDetail) {
      return !premiumDetail.isEnabled
    },
    async loadCompanyUserList () {
      this.loadingUserList = true
      try {
        const params = {
          company_id: this.companyId,
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          order: this.order,
          type: 'premium_accounts'
        }
        const resp = await this.getUserList(params)
        if (resp) {
          _.forEach(resp.account_list, account => {
            addI18nProperty(account, 'companyName', 'company.cn_name', 'company.name')
          })
          this.userList = resp.account_list
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loadingUserList = false
      }
    },
    async handleClickViewUser () {
      this.showCompanyUserList = true
      this.loadCompanyUserList()
    },
    handlePaginationChange () {
      this.loadCompanyUserList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.loadCompanyUserList()
    },
    async onEnabledChange (status, row) {
      this.loadingUserList = true
      try {
        const data = {
          account_id: row.id,
          status: status,
          account: {
            company_id: row.company.id
          }
        }
        const resp = await this.updateUser(data)
        if (resp && resp.id) {
          this.$message.success(this.$t('admin.company.userManagement.message.optionSuccess'))
          row.status = resp.status === '1'
        } else {
          this.$message.error(this.$t('admin.company.userManagement.message.optionField'))
        }
      } finally {
        this.loadingUserList = false
      }
    },
    handleSelectPrimaryAdsImage () {
      this.$refs.primaryAdsFileSelector[0].click()
    },
    async handlePrimaryAdsImageChange () {
      const file = this.$refs.primaryAdsFileSelector[0].files[0]
      if (!file) {
        return
      }
      if (!this.validateImage(file)) {
        return
      }
      const imgBase64 = await imageToBase64(window.URL.createObjectURL(file))
      this.companyAdsInfo.ads_primary_image_info.image_base64 = imgBase64
      this.companyAdsInfo.ads_primary_image_info.image = imgBase64
      this.companyAdsInfo.ads_primary_image_info.image_file_name = file.name
    },
    handleSelectSubAdsImage () {
      this.$refs.subAdsFileSelector[0].click()
    },
    async handleSubAdsImageChange () {
      const file = this.$refs.subAdsFileSelector[0].files[0]
      if (!file) {
        return
      }
      if (!this.validateImage(file)) {
        return
      }
      const imgBase64 = await imageToBase64(window.URL.createObjectURL(file))
      this.companyAdsInfo.ads_sub_image_info.image_base64 = imgBase64
      this.companyAdsInfo.ads_sub_image_info.image = imgBase64
      this.companyAdsInfo.ads_sub_image_info.image_file_name = file.name
    },
    validateImage (file) {
      if (!_.includes(this.acceptImageFileTypeList, file.type)) {
        this.$message.error({
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongPhotoFileType`, { imageType: 'jpg, png' })
        })
        return false
      }
      return true
    },
    beforeUnloadHandler (e) {
      if (this.validatePremiumHasChange()) {
        // 必须要这段代码阻止默认行为，提示的弹窗才会出来
        event.preventDefault()
        e.returnValue = ''
      }
    },
    validatePremiumHasChange () {
      if (this.originPremiumData.ads_image !== this.companyAdsInfo.ads_primary_image_info.image || this.originPremiumData.ads_image_sub !== this.companyAdsInfo.ads_sub_image_info.image || this.originPremiumData.ads_target_link !== this.companyAdsInfo.ads_target_link) {
        return true
      }
      if (!_.isEqual(_.sortBy(this.originPremiumData.postJobSelectedAccountList), _.sortBy(this.postJobSelectedAccountList)) || !_.isEqual(_.sortBy(this.originPremiumData.postJobSelectedChannelList), _.sortBy(this.postJobSelectedChannelList))) {
        return true
      }
      const originCompanyPremium = this.originPremiumData.company_premium
      let isChange = false
      _.forEach(originCompanyPremium, (premium, key) => {
        if (isChange) {
          return
        }
        const currentPremium = _.get(this.companyPremium, key)
        if (key === 'rate-management') {
          isChange = premium.expire_date !== currentPremium.expire_date || currentPremium.interview_scheduling !== premium.interview_scheduling || currentPremium.invite_hm_review !== premium.invite_hm_review || currentPremium.quickly_create_teams_meeting !== premium.quickly_create_teams_meeting || currentPremium.view_hiring_manager_availability !== premium.view_hiring_manager_availability
          return
        }
        if (key === 'post_job') {
          isChange = premium.expire_date !== currentPremium.expire_date
          return
        }
        if (key === 'sms_send') {
          isChange = premium.value !== currentPremium.value || premium.expire_date !== currentPremium.expire_date || currentPremium.consent_sms_send !== premium.consent_sms_send || currentPremium.other_sms_send !== premium.other_sms_send
          return
        }
        if (key === 'color_customization') {
          // isChange = premium.expire_date !== currentPremium.expire_date || currentPremium.color !== premium.color || currentPremium.hidden_web_page_bottom_statement !== premium.hidden_web_page_bottom_statement
          isChange = premium.expire_date !== currentPremium.expire_date || currentPremium.hidden_web_page_bottom_statement !== premium.hidden_web_page_bottom_statement
          return
        }
        if (premium.value !== currentPremium.value || premium.expire_date !== currentPremium.expire_date) {
          isChange = true
        }
      })
      return isChange
    },
    handleCancelBtnClick () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.push({ path: `/v-admin/client-management/company/${this.companyId}/`, query: this.$route.query })
      }
    },
    validatePremium () {
      let validate = true
      const numRegex = /^[1-9]\d*|0$/
      _.forEach(this.companyPremium, (premium, key) => {
        if (premium.isEnabled && !premium.expire_date) {
          validate = false
          this.$message({
            type: 'warning',
            message: this.$t(`${this.i18nPrefix}.message.expireDateError`, { name: premium.name })
          })
        }
        if (_.includes(_.keys(HAS_NUMBER_VALIDATE_PREMIUM_MAP), key)) {
          const valueKey = HAS_NUMBER_VALIDATE_PREMIUM_MAP[key]
          const numData = _.get(premium, valueKey)
          if (!numData) {
            return
          }
          if (numData && !numRegex.test(_.parseInt(numData))) {
            validate = false
            this.$message({
              type: 'warning',
              message: this.$t(`${this.i18nPrefix}.message.wrongNumberFormat`, { name: premium.name })
            })
          }
        }
        if (!validate) {
          return false
        }
      })
      return validate
    },
    async handleSaveBtnClick () {
      if (!this.validatePremium()) {
        return
      }
      const companyPremium = this.companyPremium
      if (companyPremium.post_job?.isEnabled) {
        companyPremium.post_job.value = JSON.stringify({
          account_ids: this.postJobSelectedAccountList.join(','),
          channel_ids: this.postJobSelectedChannelList.join(','),
          manual_channel_ids: this.manualPostJobSelectedChannelList.join(',')
        })
      }
      // if (companyPremium.color_customization?.isEnabled) {
      //   companyPremium.color_customization.value = JSON.stringify({
      //     color: companyPremium.color_customization.color,
      //     hidden_web_page_bottom_statement: companyPremium.color_customization.hidden_web_page_bottom_statement
      //   })
      // }
      const rateManagementPremiumInfo = companyPremium['rate-management']
      if (rateManagementPremiumInfo?.isEnabled) {
        companyPremium['rate-management'].value = JSON.stringify({
          premium_interview_scheduling: rateManagementPremiumInfo.interview_scheduling,
          premium_invite_hm_review: rateManagementPremiumInfo.invite_hm_review,
          premium_quickly_create_teams_meeting: rateManagementPremiumInfo.quickly_create_teams_meeting,
          premium_view_hiring_manager_availability: rateManagementPremiumInfo.view_hiring_manager_availability
        })
      }
      this.loading = true
      try {
        const premiumInfo = {
          company_id: this.companyId,
          premium_info: companyPremium,
          company_ads_info: this.companyAdsInfo
        }
        const resp = await this.saveCompanyPremium(premiumInfo)
        if (resp) {
          window.removeEventListener('beforeunload', this.beforeUnloadHandler)
          this.handleCancelBtnClick()
        }
      } finally {
        this.loading = false
      }
    },
    handleClickResetExpireDate () {
      this.showResetExpireDate = true
    },
    handleAllPremiumExpireDate () {
      if (!this.currentPremiumExpireDate) {
        return
      }
      _.forEach(this.companyPremium, (val, key) => {
        val.expire_date = this.currentPremiumExpireDate
      })
      this.$message({
        type: 'success',
        message: this.$t(`${this.i18nPrefix}.message.resetExpireDateSuccess`)
      })
      this.showResetExpireDate = false
    }
  }
}
</script>
<style lang="scss" scoped>
.company-premium-management {
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .company-name {
      .name {
        font-size: 20px;
        font-weight: bold;
        vertical-align: bottom;
      }
      .tip {
        font-size: 12px;
        vertical-align: bottom;
        line-height: 1;
        margin-left: 10px;
      }
    }
    .rest-expire-date {
      .reset-expire-date-btn {
        background: #0096cc;
        color: #fff;
        border: 1px solid #0096cc;
        margin-left: 24px;
        line-height: 14px;
        padding: 10px 30px;
      }
    }
  }
  .premium-collapse {
    .premium-collapse-item {
      ::v-deep {
        .el-collapse-item__header {
          font-size: 16px;
          color: #303133;
          font-weight: 500;
        }
        .el-collapse-item__wrap {
          .el-collapse-item__content {
            padding-bottom: 10px;
          }
        }
      }
      .premium-item {
        padding: 6px 16px;
        line-height: 32px;
        &:hover {
          background-color: #e6f5fa;
        }
        .user-num-input {
          width: 100px;
        }
        .user-num-btn {
          margin-left: 20px;
          font-size: 14px;
          border: 0;
          cursor: pointer;
          line-height: 14px;
          padding: 9px 22px;
          background: #0096cc;
          color: #fff;
          font-weight: 700;
        }
        .user-num-btn:hover {
          background: #00aaff;
        }
        .company-list-first-level {
          .upload-setting {
            margin-bottom: 14px;
            .title {
              display: inline-block;
              vertical-align: middle;
              .label {
                display: inline-block;
                width: 200px;
              }
              .image-selector {
                display: inline-block;
                vertical-align: middle;
                .select-file {
                  font-size: 12px;
                  border: 0;
                  cursor: pointer;
                  line-height: 12px;
                  padding: 8px 24px;
                  background: #0096cc;
                  color: #fff;
                  font-weight: 700;
                }
                .file-selector {
                  display: none;
                }
                .tip {
                  display: inline-block;
                  vertical-align: middle;
                  margin-left: 8px;
                }
              }
            }
            .image {
              margin-left: 200px;
              margin-top: 14px;
              .pre-image {
                max-width: 245px;
                height: 75px;
              }
            }
          }
          .primary-image {}
          .sub-image {}
        }
        .company-list-second-level {
          .label {
            width: 200px;
            display: inline-block;
          }
          .link-content {
            display: inline-block;
            vertical-align: top;
            .link {
              width: 300px;
            }
            .tip {
              display: block;
            }
          }
        }

        .sms-send-content {
          .el-checkbox + .el-checkbox {
            margin-left: 20px;
          }
        }

        .auto-post-job {
          .set-email {
            margin-left: 22px;
            display: flex;
            align-items: center;
            .label {
              width: 200px;
            }

            .select-account {
              width: 50%;
            }
          }

          .select-channels {
            margin-left: 22px;
            margin-top: 12px;
            display: flex;
            align-items: center;
            .label {
              width: 200px;
            }
            .select-channel {
              width: 50%;
            }
          }
        }

        .manual-post-job {
          .select-channels {
            margin-left: 22px;
            display: flex;
            align-items: center;
            .label {
              width: 200px;
            }
            .select-channel {
              width: 50%;
            }
          }
        }

        .color-custom-content {
          .set-color {
            margin-right: 50px;
            display: inline-block;
            .label {
              line-height: 19px;
              vertical-align: middle;
            }
            .el-color-picker {
              margin-left: 20px;
              vertical-align: middle;
            }
          }
        }

        .set-agency-management-days {
          .label {
          }
          .days {
            width: 8%;
            margin: 0 20px;
          }
        }

        .set-referral-days {
          .days {
            width: 8%;
            margin: 0 20px;
          }
        }

        .data-purge-type {
          margin-bottom: 10px;
        }

        .set-data-purge-days {
          .label {
          }
          .days {
            width: 8%;
            margin: 0 20px;
          }
        }
      }
    }
  }
  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .footer-btn {
      margin-top: 40px;
      font-size: 14px;
      border: 0;
      cursor: pointer;
      margin-left: 24px;
      line-height: 14px;
      padding: 10px 30px;
      background: #0096cc;
      color: #fff;

      &:first-child {
        margin-left: 0;
        background: #a9a9a9;
        color: #fff;
        border: 1px solid #a9a9a9;
      }

      &:last-child {
        background: #0096cc;
        color: #fff;
        border: 1px solid #0096cc;
      }
    }
  }
  .rest-expire-date-dialog {
    ::v-deep {
      .el-dialog {
        width: 35%;
        .rest-expire-date-picker {
          width: 100%;
        }
      }
    }
  }

  .company-user-list-dialog {
    .user-management-list {
      ::v-deep {
        .list-container {
          .el-pagination {
            overflow: auto;
          }
        }
      }
    }
  }
}
</style>
